import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { harmonateTokens as ht } from '../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateAccentLinkSx,
} from '../../theme/harmonateStyles';

const DocumentTypeFiles = () => {
  const { type } = useParams(); // Get the document type from the URL
  const navigate = useNavigate();
  const location = useLocation();
  const { categorizedFiles } = location.state || { categorizedFiles: {} };
  const decodedType = decodeURIComponent(type);

  const [currentView, setCurrentView] = useState('fileList');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedFileNames, setSelectedFileNames] = useState([]);

  const files = categorizedFiles[decodedType] || [];

  const handleBack = () => {
    navigate('/');
  };

  const handleFileClick = (file) => {
    if (file.detectedType === 'PDF') {
      setSelectedFiles([file.url]);
      setSelectedFileNames([file.name]);
      setCurrentView('pdfViewer');
    }
  };

  const handleBackToFileList = () => {
    setCurrentView('fileList');
    setSelectedFiles([]);
    setSelectedFileNames([]);
  };

  const PDFViewer = ({ initialFiles = [], initialFileNames = [], onBack }) => {
    const [pdfFiles, setPdfFiles] = useState(initialFiles);
    const [error, setError] = useState(null);
    const [fileNames, setFileNames] = useState(initialFileNames);

    useEffect(() => {
      return () => {
        pdfFiles.forEach(file => {
          if (file && file.startsWith('blob:')) {
            URL.revokeObjectURL(file);
          }
        });
      };
    }, [pdfFiles]);

    return (
      <Box sx={{ ...harmonatePageSx, minHeight: '100vh' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button 
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={harmonateAccentLinkSx}
          >
            Back to Files
          </Button>
        </Box>
        
        <div className="pdf-viewer-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'left', maxWidth: '100%' }}>
          {error && (
            <div style={{ color: ht.red, margin: '10px 0' }}>{error}</div>
          )}
          
          {pdfFiles.length > 0 ? (
            <Box sx={{ maxWidth: '800px', width: '100%' }}>
              {pdfFiles.map((pdfFile, index) => (
                <Box key={index} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1, color: ht.white, fontWeight: 600 }}>
                    {fileNames[index] || `Document ${index + 1}`}
                  </Typography>
                  <div style={{ width: '100%', height: '800px', border: `1px solid ${ht.line}`, borderRadius: ht.cardRadius, overflow: 'hidden' }}>
                    <iframe 
                      src={`${pdfFile}#toolbar=1&navpanes=1&scrollbar=1`}
                      title={`PDF Viewer - ${fileNames[index] || `Document ${index + 1}`}`}
                      style={{ width: '100%', height: '100%', border: 'none' }}
                    />
                  </div>
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ p: 2.5, textAlign: 'center', border: `1px solid ${ht.line}`, borderRadius: ht.cardRadius, maxWidth: '800px', width: '100%', bgcolor: ht.white, color: ht.text }}>
              {!error && 'No PDF files to display'}
            </Box>
          )}
        </div>
      </Box>
    );
  };

  const renderFileList = () => {
    return (
      <Box sx={{ ...harmonatePageSx, minHeight: '100vh' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button 
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={harmonateAccentLinkSx}
          >
            Back to Workflows
          </Button>
        </Box>

        <Typography variant="h6" sx={{ ...harmonatePageTitleSx, mb: 3 }}>
          {decodedType} Files
        </Typography>

        <Paper elevation={0} sx={{ ...harmonateCardSx, flex: 1 }}>
          <Box sx={{ 
            display: 'flex', 
            bgcolor: ht.white, 
            py: 1.5,
            px: 2,
            borderBottom: `1px solid ${ht.line}`,
          }}>
            <Box sx={{ flex: 1, fontWeight: 700, fontSize: '0.625rem', color: ht.text }}>File Name</Box>
            <Box sx={{ width: 120, textAlign: 'center', fontWeight: 700, fontSize: '0.625rem', color: ht.text }}>Type</Box>
          </Box>

          {files.length > 0 ? (
            files.map((file, index) => (
              <Box 
                key={index}
                onClick={() => handleFileClick(file)}
                sx={{ 
                  display: 'flex', 
                  py: 1.5,
                  px: 2,
                  borderBottom: `1px solid ${ht.line}`,
                  bgcolor: ht.white,
                  '&:hover': { bgcolor: ht.secondaryTab, cursor: 'pointer' }
                }}
              >
                <Box sx={{ flex: 1, fontSize: '0.75rem', color: ht.text }}>{file.name}</Box>
                <Box sx={{ width: 120, textAlign: 'center', fontSize: '0.75rem', color: ht.text }}>{file.detectedType}</Box>
              </Box>
            ))
          ) : (
            <Box sx={{ 
              py: 2, 
              px: 2, 
              textAlign: 'center', 
              color: ht.text,
              bgcolor: ht.white,
            }}>
              No files found for this document type.
            </Box>
          )}
        </Paper>
      </Box>
    );
  };

  return currentView === 'fileList' 
    ? renderFileList() 
    : <PDFViewer initialFiles={selectedFiles} initialFileNames={selectedFileNames} onBack={handleBackToFileList} />;
};

export default DocumentTypeFiles;
